import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError, downloadPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDMY, TIME_OPTIONS } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SignaturePad } from "@/components/SignaturePad";
import { SampleDataBadge } from "@/components/SampleDataBadge";
import { toast } from "sonner";
import { Loader2, FileDown, CheckCircle2, PenLine, Info, RotateCcw, Wand2, ChevronLeft, ChevronRight } from "lucide-react";

const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland", dateStyle: "medium", timeStyle: "short", hour12: false }); }
  catch { return iso?.slice(0, 16)?.replace("T", " ") || ""; }
};

// Mon=0 .. Sun=6 for an ISO date
const weekdayIdx = (iso) => (new Date(`${iso}T00:00:00`).getDay() + 6) % 7;


const shiftDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export function WeeklyTimesheetDialog({ open, onOpenChange, mode, cleanerId, date: dateProp }) {
  const { t } = useTranslation();
  // Cleaners can page back through past (closed) weeks; the prop is the starting week.
  const [date, setDate] = useState(dateProp);
  useEffect(() => { if (open) setDate(dateProp); }, [open, dateProp]);
  const canGoForward = mode === "cleaner" && shiftDays(date, 7) <= dateProp;
  const qc = useQueryClient();
  const { motel } = useAuth();
  const defaultStarts = motel?.payroll?.default_start_times || [];
  const [sig, setSig] = useState("");
  const [pp, setPp] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [reason, setReason] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const key = mode === "cleaner" ? "my" : cleanerId;
  const { data, isLoading } = useQuery({
    queryKey: ["weekly-ts", key, date],
    enabled: open,
    queryFn: async () =>
      mode === "cleaner"
        ? (await api.get(`/my/timesheet?date=${date}`)).data
        : (await api.get(`/timesheets/weekly?date=${date}&cleaner_id=${cleanerId}`)).data,
  });

  const signMut = useMutation({
    mutationFn: () =>
      mode === "cleaner"
        ? api.post(`/my/timesheet/sign`, { date, signature: sig })
        : api.post(`/timesheets/${cleanerId}/authorise`, { date, signature: sig, pp }),
    onSuccess: () => {
      toast.success(mode === "cleaner" ? t("Timesheet signed") : t("Timesheet authorised"));
      setSig(""); setPp(false);
      qc.invalidateQueries({ queryKey: ["weekly-ts"] });
      qc.invalidateQueries({ queryKey: ["weekly-all"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const reopenMut = useMutation({
    mutationFn: () => api.post(`/timesheets/${cleanerId}/reopen`, { date }),
    onSuccess: () => {
      toast.success(t("Timesheet reopened — signatures cleared for a fresh sign-off"));
      qc.invalidateQueries({ queryKey: ["weekly-ts"] });
      qc.invalidateQueries({ queryKey: ["weekly-all"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const { data: labelData } = useQuery({
    queryKey: ["ts-labels"],
    enabled: open,
    queryFn: async () => (await api.get(`/timesheets/labels`)).data,
  });

  const labelPath = mode === "cleaner" ? `/my/timesheet/label` : `/timesheets/${cleanerId}/label`;
  const labelMut = useMutation({
    mutationFn: ({ day, label }) => api.post(labelPath, { date, day, label }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weekly-ts"] });
      qc.invalidateQueries({ queryKey: ["weekly-all"] });
      qc.invalidateQueries({ queryKey: ["ts-labels"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const pdfPath =
    mode === "cleaner"
      ? `/my/timesheet/pdf?date=${date}`
      : `/timesheets/weekly/pdf?date=${date}&cleaner_id=${cleanerId}`;

  const createPdf = async () => {
    setPdfBusy(true);
    try {
      const name = (data?.cleaner_name || "timesheet").replace(/\s+/g, "_");
      await downloadPdf(pdfPath, `timesheet_${name}_${data?.week_end || ""}.pdf`);
    } catch {
      toast.error(t("Could not create the PDF. Please try again."));
    } finally {
      setPdfBusy(false);
    }
  };

  const cleanerSigned = !!data?.cleaner_signature;
  const authorised = !!data?.manager_signature;
  const canAct = mode === "cleaner" ? !cleanerSigned : (!authorised && (cleanerSigned || pp));
  const canEdit = !authorised;
  const editPath = mode === "cleaner" ? `/my/timesheet/edit` : `/timesheets/${cleanerId}/edit`;

  const val = (r, f) => (r.edits?.[f] !== undefined ? r.edits[f] : r[f]);
  const isEdited = (r, f) => r.edits?.[f] !== undefined && r.edits[f] !== r[f];
  const single = (r, f) =>
    isEdited(r, f) ? (
      <span><span className="text-muted-foreground line-through">{r[f] || "—"}</span>
        <span className="block font-semibold text-indigo-700">{val(r, f) || "—"}</span></span>
    ) : (<span>{val(r, f) || "—"}</span>);
  const pair = (r, a, b) => {
    const o = r[a] ? (r[b] ? `${r[a]}–${r[b]}` : r[a]) : "—";
    const n = val(r, a) ? (val(r, b) ? `${val(r, a)}–${val(r, b)}` : val(r, a)) : "—";
    return isEdited(r, a) || isEdited(r, b) ? (
      <span><span className="text-muted-foreground line-through">{o}</span>
        <span className="block font-semibold text-indigo-700">{n}</span></span>
    ) : (<span>{n}</span>);
  };
  const tinput = (r, f) => {
    const dft = f === "start" ? (defaultStarts[weekdayIdx(r.date)] || "")
      : (f === "finish" ? (r.default_finish || "") : "");
    const current = draft[r.date]?.[f] ?? val(r, f) ?? "";
    return (
      <Select
        value={current}
        onValueChange={(v) => setDraft((d) => ({ ...d, [r.date]: { ...d[r.date], [f]: v === "__blank" ? "" : v } }))}
      >
        <SelectTrigger className="h-7 w-[78px] px-2 text-xs" data-testid={`edit-${r.date}-${f}`}>
          <SelectValue placeholder={dft ? dft : "—"} />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          <SelectItem value="__blank">—</SelectItem>
          {TIME_OPTIONS.filter(Boolean).map((tm) => (
            <SelectItem key={tm} value={tm}>{tm}{dft && tm === dft ? ` ${t("(default)")}` : ""}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const fillDayDefault = (r) => {
    const dft = defaultStarts[weekdayIdx(r.date)] || "";
    if (!dft) return;
    setDraft((d) => ({ ...d, [r.date]: { ...d[r.date], start: dft } }));
  };

  const fillDayFinish = (r) => {
    if (!r.default_finish) return;
    setDraft((d) => ({ ...d, [r.date]: { ...d[r.date], finish: r.default_finish } }));
  };

  // Bulk Week Edit — set the same start time across every day of the week at once.
  const setAllStarts = (v) => {
    if (!data?.days) return;
    const start = v === "__blank" ? "" : v;
    setDraft((d) => {
      const next = { ...d };
      for (const r of data.days) next[r.date] = { ...next[r.date], start };
      return next;
    });
  };

  const saveEdits = async () => {
    const daysWithEdits = Object.keys(draft);
    if (!daysWithEdits.length) { setEditMode(false); return; }
    if (!reason.trim()) { toast.error(t("Please add a reason for the change.")); return; }
    try {
      let resign = false;
      for (const day of daysWithEdits) {
        const res = await api.post(editPath, { date, day, fields: draft[day], reason: reason.trim() });
        if (res.data.resign_required) resign = true;
      }
      toast.success(
        mode === "cleaner"
          ? (resign ? t("Timesheet updated — please sign again") : t("Timesheet updated"))
          : (resign ? t("Timesheet corrected — cleaner must re-sign") : t("Timesheet corrected")),
      );
      setDraft({}); setReason(""); setEditMode(false);
      qc.invalidateQueries({ queryKey: ["weekly-ts"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="weekly-timesheet-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {data ? `${t("TIMESHEET")} – ${data.motel_name}` : t("Timesheet")}
            <SampleDataBadge />
          </DialogTitle>
          <DialogDescription>
            {data ? `${data.cleaner_name} · ${t("week ending")} ${fmtDMY(data.week_end)}` : t("Weekly timesheet")}
          </DialogDescription>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-1">
                {mode === "cleaner" && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditMode(false); setDraft({}); setDate(shiftDays(date, -7)); }} data-testid="ts-prev-week" title={t("Previous week")}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                )}
                <span>
                  <span className="font-semibold">{data.cleaner_name}</span>
                  <span className="text-muted-foreground"> · {t("Week ending")} {fmtDMY(data.week_end)}</span>
                  {mode === "cleaner" && authorised && (
                    <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="ts-closed-badge">{t("Closed")}</span>
                  )}
                </span>
                {mode === "cleaner" && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={!canGoForward} onClick={() => { setEditMode(false); setDraft({}); setDate(shiftDays(date, 7)); }} data-testid="ts-next-week" title={t("Next week")}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={createPdf} disabled={pdfBusy} data-testid="ts-pdf-btn">
                {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {t("Create PDF")}
              </Button>
              {mode === "manager" && authorised && (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => reopenMut.mutate()} disabled={reopenMut.isPending} data-testid="ts-reopen-btn">
                  {reopenMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} {t("Reopen")}
                </Button>
              )}
              {canEdit && !editMode && (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="ts-edit-btn" className="gap-1.5">
                  <PenLine className="h-4 w-4" /> {t("Edit times")}
                </Button>
              )}
              {editMode && (
                <>
                  <Button size="sm" onClick={saveEdits} data-testid="ts-edit-save" className="gap-1.5">
                    <CheckCircle2 className="h-4 w-4" /> {t("Save")}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setDraft({}); setReason(""); setEditMode(false); }} data-testid="ts-edit-cancel">{t("Cancel")}</Button>
                </>
              )}
            </div>

            {editMode && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-secondary/30 px-3 py-2 text-xs" data-testid="bulk-week-edit">
                <span className="font-semibold text-muted-foreground">{t("Set all start times")}</span>
                <Select onValueChange={setAllStarts}>
                  <SelectTrigger className="h-7 w-[110px] px-2 text-xs" data-testid="bulk-start-select">
                    <SelectValue placeholder={t("Choose…")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__blank">{t("— (clear all)")}</SelectItem>
                    {TIME_OPTIONS.filter(Boolean).map((tm) => (<SelectItem key={tm} value={tm}>{tm}</SelectItem>))}
                  </SelectContent>
                </Select>
                <span className="text-muted-foreground">{t("applies to every day this week")}</span>
              </div>
            )}

            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs" data-testid="weekly-grid">
                <thead className="bg-primary text-primary-foreground">
                  <tr>
                    {["Day", "Date", "Start", "Break", "Lunch", "Break2", "Finish", "Total"].map((h) => (
                      <th key={h} className="px-2 py-2 text-left font-bold">{t(h === "Break2" ? "Break" : h)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((r) => (
                    <tr key={r.date} className="border-t border-border odd:bg-secondary/30 align-top">
                      <td className="px-2 py-1.5 font-medium">{t(r.day)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          {fmtDMY(r.date)}
                          {r.holiday && (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`holiday-badge-${r.date}`} title={`NZ public holiday: ${r.holiday}`}>{r.holiday}</span>
                          )}
                        </div>
                      </td>
                      {editMode ? (
                        <>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              {tinput(r, "start")}
                              {defaultStarts[weekdayIdx(r.date)] && (
                                <button
                                  type="button"
                                  title={`Fill from default (${defaultStarts[weekdayIdx(r.date)]})`}
                                  onClick={() => fillDayDefault(r)}
                                  data-testid={`fill-default-${r.date}`}
                                  className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                                >
                                  <Wand2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">{tinput(r, "break1_start")} {tinput(r, "break1_end")}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap">{tinput(r, "lunch_start")} {tinput(r, "lunch_end")}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap">{tinput(r, "break2_start")} {tinput(r, "break2_end")}</td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              {tinput(r, "finish")}
                              {r.default_finish && (
                                <button
                                  type="button"
                                  title={`Fill from estimate (${r.default_finish})`}
                                  onClick={() => fillDayFinish(r)}
                                  data-testid={`fill-finish-${r.date}`}
                                  className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                                >
                                  <Wand2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 font-semibold">{r.total_hours ? r.total_hours.toFixed(2) : "—"}</td>
                        </>
                      ) : (!r.start && !r.finish && !r.total_hours) ? (
                        <>
                          <td className="px-2 py-1.5" colSpan={5} data-testid={`label-cell-${r.date}`}>
                            {canEdit ? (
                              <Select
                                value={r.label || "__none"}
                                onValueChange={(v) => labelMut.mutate({ day: r.date, label: v === "__none" ? "" : v })}
                              >
                                <SelectTrigger className={`h-7 w-[210px] px-2 text-xs ${r.label ? "border-violet-300 bg-violet-50 font-medium text-violet-700" : "text-muted-foreground"}`} data-testid={`label-select-${r.date}`}>
                                  <SelectValue placeholder={t("+ Add leave / label")} />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                  <SelectItem value="__none">{t("— No label")}</SelectItem>
                                  {(labelData?.recent?.length > 0) && (
                                    <SelectGroup>
                                      <SelectLabel>{t("Recently used")}</SelectLabel>
                                      {labelData.recent.map((l) => (<SelectItem key={`r-${l}`} value={l}>{l}</SelectItem>))}
                                    </SelectGroup>
                                  )}
                                  <SelectGroup>
                                    <SelectLabel>{t("All labels")}</SelectLabel>
                                    {(labelData?.all || []).filter((l) => !(labelData?.recent || []).includes(l)).map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            ) : r.label ? (
                              <span className="rounded bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">{r.label}</span>
                            ) : <span className="text-muted-foreground">—</span>}
                            {r.label_auto && !r.holiday && (
                              <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-500" data-testid={`label-auto-${r.date}`} title="Filled automatically from this cleaner's roster">{t("from roster")}</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 font-semibold">—</td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-1.5">
                            {single(r, "start")}
                            {r.start_defaulted && (
                              <span className="ml-1 rounded bg-sky-100 px-1 py-0.5 text-[9px] font-bold uppercase text-sky-700" data-testid={`start-default-tag-${r.date}`} title="Filled from default start time">{t("default")}</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">{pair(r, "break1_start", "break1_end")}</td>
                          <td className="px-2 py-1.5">{pair(r, "lunch_start", "lunch_end")}</td>
                          <td className="px-2 py-1.5">{pair(r, "break2_start", "break2_end")}</td>
                          <td className="px-2 py-1.5">
                            {single(r, "finish")}
                          </td>
                          <td className="px-2 py-1.5 font-semibold">{r.total_hours ? r.total_hours.toFixed(2) : "—"}</td>
                        </>
                      )}
                    </tr>
                  ))}
                  <tr className="border-t-2 border-primary bg-indigo-50 font-bold">
                    <td className="px-2 py-2" colSpan={7}>{t("Total Hours Worked")}</td>
                    <td className="px-2 py-2" data-testid="ts-total-hours">{data.total_hours_worked.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {mode === "manager" && data.lunch_round_paid_week_min != null && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="lunch-round-paid">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {t("Lunch is rounded down to the nearest 15 min.")}{" "}
                  <span className="font-semibold" data-testid="lunch-round-paid-total">
                    {t("Extra lunch paid this week: {{min}} min", { min: data.lunch_round_paid_week_min })}
                  </span>{" "}
                  <span className="text-amber-600">{t("(manager only)")}</span>
                </span>
              </div>
            )}


            {editMode && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="edit-reason-box">
                <label className="mb-1 block text-xs font-semibold text-amber-800">{t("Reason for change (required)")}</label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("e.g. Forgot to clock out — actually finished at 2:30pm")} data-testid="edit-reason-input" />
              </div>
            )}

            {!editMode && data.days.some((r) => r.edit_reason) && (
              <div className="rounded-md border p-3" data-testid="corrections-list">
                <div className="label-caps mb-1 flex items-center gap-1 text-muted-foreground"><Info className="h-3 w-3" /> {t("Corrections")}</div>
                <ul className="space-y-1 text-xs">
                  {data.days.filter((r) => r.edit_reason).map((r) => (
                    <li key={r.date}>
                      <span className="font-semibold">{r.day} {fmtDMY(r.date)}:</span> {r.edit_reason}
                      {r.edited_by ? ` — ${r.edited_by}${r.edited_role === "manager" ? " (manager)" : ""}` : ""}
                      {r.edited_at ? <span className="text-muted-foreground"> · {fmtDateTime(r.edited_at)}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Signatures */}
            <div className="grid gap-4 sm:grid-cols-2">
              <SigBlock label={t("Employee signature")} img={data.cleaner_signature} name={data.cleaner_name} when={data.cleaner_signed_at} pp={data.cleaner_signed_pp} ppBy={data.cleaner_signed_by} />
              <SigBlock label={t("Manager signature")} img={data.manager_signature} name={data.manager_name} when={data.authorised_at} />
            </div>

            {canAct && (
              <div className="rounded-md border border-dashed p-3" data-testid="sign-section">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <PenLine className="h-4 w-4" />
                  {mode === "cleaner" ? t("Sign your timesheet") : (pp ? t("Sign on behalf of {{name}} (PP) & authorise", { name: data.cleaner_name }) : t("Authorise this timesheet"))}
                </div>
                {mode === "manager" && pp && (
                  <p className="mb-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid="pp-note">
                    {t("Your signature will be recorded in the employee area marked \"pp\" (on their behalf) and as the manager authorisation.")}
                  </p>
                )}
                <SignaturePad onChange={setSig} />
                <div className="mt-2 flex justify-end">
                  <Button
                    onClick={() => signMut.mutate()}
                    disabled={!sig || signMut.isPending}
                    data-testid="ts-sign-submit"
                    className="gap-2"
                  >
                    {signMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {mode === "cleaner" ? t("Sign timesheet") : (pp ? t("PP sign & authorise") : t("Authorise"))}
                  </Button>
                </div>
              </div>
            )}
            {mode === "manager" && !cleanerSigned && !authorised && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800" data-testid="await-cleaner-sign">
                {!pp && <p>{t("Waiting for {{name}} to sign before you can authorise.", { name: data.cleaner_name })}</p>}
                <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 text-xs font-semibold">
                  <input type="checkbox" checked={pp} onChange={(e) => { setPp(e.target.checked); setSig(""); }} data-testid="pp-sign-toggle" className="h-4 w-4" />
                  {t("Sign on behalf of the employee (pp)")}
                </label>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SigBlock({ label, img, name, when, pp, ppBy }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border p-3">
      <div className="label-caps mb-1 flex items-center gap-2 text-muted-foreground">
        {label}
        {pp && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700" data-testid="pp-badge" title={t("Signed on the employee's behalf (per procurationem)")}>pp</span>
        )}
      </div>
      {img ? (
        <img src={img} alt={label} className="h-16 w-full rounded border bg-white object-contain" />
      ) : (
        <div className="flex h-16 items-center justify-center rounded border border-dashed text-xs text-muted-foreground">{t("Not signed")}</div>
      )}
      <div className="mt-1 text-xs text-muted-foreground">
        {pp ? <span className="font-semibold">pp </span> : null}{name || ""}{when ? ` · ${when.slice(0, 10)}` : ""}
        {pp && ppBy ? <span className="block text-[11px]">{t("signed by {{by}} on their behalf", { by: ppBy })}</span> : null}
      </div>
    </div>
  );
}
